// Pop-out wallet panel: balance, free trial countdown, top-up, auto-refill,
// current plan, manage billing, transaction history.
import { useState } from "react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Loader2, Wallet, ExternalLink } from "lucide-react";
import { useWallet } from "@/hooks/useWallet";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { getStripeEnvironment, isPaymentsConfigured } from "@/lib/stripe";
import StripeEmbeddedCheckoutBox from "./StripeEmbeddedCheckout";
import { PaymentTestModeBanner } from "./PaymentTestModeBanner";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialView?: "wallet" | "topup" | "plans";
}

const TIERS = [
  { id: "starter", priceId: "sub_starter_monthly", name: "Starter", price: "$20/mo", credits: "$22 credits" },
  { id: "pro", priceId: "sub_pro_monthly", name: "Pro", price: "$50/mo", credits: "$57.50 credits" },
  { id: "power", priceId: "sub_power_monthly", name: "Power", price: "$100/mo", credits: "$120 credits" },
];

export default function WalletSheet({ open, onOpenChange, initialView = "wallet" }: Props) {
  const { user } = useAuth();
  const { wallet, subscription, recentTx, isTrialing, refresh } = useWallet();
  const [view, setView] = useState<"wallet" | "topup" | "plans">(initialView);
  const [activeCheckout, setActiveCheckout] = useState<{ kind: "topup" | "subscription"; priceId: string } | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);

  if (!user) return null;

  const balanceUsd = ((wallet?.balance_cents ?? 0) / 100).toFixed(2);
  const subCreditsUsd = ((subscription?.monthly_credits_remaining_cents ?? 0) / 100).toFixed(2);
  const freeMins = Math.floor((wallet?.free_seconds_remaining ?? 0) / 60);
  const freeSecs = (wallet?.free_seconds_remaining ?? 0) % 60;

  const startCheckout = (kind: "topup" | "subscription", priceId: string) => {
    if (!isPaymentsConfigured()) {
      toast.error("Payments not configured");
      return;
    }
    setActiveCheckout({ kind, priceId });
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("wallet-portal", {
        body: {
          environment: getStripeEnvironment(),
          returnUrl: `${window.location.origin}/`,
        },
      });
      if (error || !data?.url) throw new Error(error?.message || "Failed to open portal");
      window.open(data.url, "_blank");
    } catch (e: any) {
      toast.error(e.message ?? "Failed to open billing portal");
    } finally {
      setPortalLoading(false);
    }
  };

  const toggleAutoRefill = async (enabled: boolean) => {
    await supabase.from("user_wallet").update({ auto_refill_enabled: enabled }).eq("user_id", user.id);
    refresh();
    if (enabled) {
      toast.success("Auto-refill on — we'll add $20 when your balance drops below $2.");
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) { setActiveCheckout(null); setView("wallet"); } }}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto p-0">
        <PaymentTestModeBanner />
        <div className="p-6">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-primary" /> Wallet
            </SheetTitle>
            <SheetDescription>
              Pay-as-you-go. Top up $20 anytime. After 30 days of regular use we'll suggest a plan that fits.
            </SheetDescription>
          </SheetHeader>

          {activeCheckout ? (
            <div className="mt-4">
              <Button variant="ghost" size="sm" onClick={() => setActiveCheckout(null)} className="mb-2">← Back</Button>
              <StripeEmbeddedCheckoutBox kind={activeCheckout.kind} priceId={activeCheckout.priceId} />
            </div>
          ) : view === "plans" ? (
            <div className="mt-6 space-y-3">
              <Button variant="ghost" size="sm" onClick={() => setView("wallet")}>← Back to wallet</Button>
              {TIERS.map((tier) => (
                <div key={tier.id} className="border border-border rounded-lg p-4 flex items-center justify-between">
                  <div>
                    <div className="font-semibold">{tier.name}</div>
                    <div className="text-xs text-muted-foreground">{tier.price} · {tier.credits}</div>
                  </div>
                  <Button size="sm" onClick={() => startCheckout("subscription", tier.priceId)}>Choose</Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-6 space-y-6">
              <div className="rounded-lg border border-border bg-card/40 p-4">
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Wallet balance</div>
                <div className="text-3xl font-bold font-mono">${balanceUsd}</div>
                {subscription?.status === "active" && (
                  <div className="text-xs text-primary mt-1">+ ${subCreditsUsd} plan credits remaining</div>
                )}
                {isTrialing && (
                  <div className="text-xs text-amber-600 dark:text-amber-400 mt-1 font-mono">
                    Free trial: {freeMins}m {freeSecs}s left
                  </div>
                )}
                <div className="flex gap-2 mt-3">
                  <Button size="sm" onClick={() => startCheckout("topup", "wallet_topup_20_usd")}>Add $20</Button>
                  {subscription?.status !== "active" && (
                    <Button size="sm" variant="outline" onClick={() => setView("plans")}>View plans</Button>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between rounded-lg border border-border p-4">
                <div>
                  <div className="text-sm font-medium">Auto-refill</div>
                  <div className="text-xs text-muted-foreground">Add $20 automatically when balance drops below $2.</div>
                </div>
                <Switch checked={wallet?.auto_refill_enabled ?? false} onCheckedChange={toggleAutoRefill} />
              </div>

              {subscription?.status === "active" && subscription.tier_id && (
                <div className="rounded-lg border border-border p-4 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium capitalize">{subscription.tier_id} plan</div>
                    {subscription.current_period_end && (
                      <div className="text-xs text-muted-foreground">
                        Renews {new Date(subscription.current_period_end).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                  <Button size="sm" variant="outline" disabled={portalLoading} onClick={openPortal}>
                    {portalLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <>Manage <ExternalLink className="w-3 h-3 ml-1" /></>}
                  </Button>
                </div>
              )}

              {wallet?.stripe_customer_id && subscription?.status !== "active" && (
                <Button variant="ghost" size="sm" onClick={openPortal} disabled={portalLoading} className="w-full">
                  {portalLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <>Manage payment methods <ExternalLink className="w-3 h-3 ml-1" /></>}
                </Button>
              )}

              <Separator />

              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Recent activity</div>
                {recentTx.length === 0 ? (
                  <div className="text-sm text-muted-foreground">No transactions yet.</div>
                ) : (
                  <div className="space-y-1.5 max-h-64 overflow-y-auto">
                    {recentTx.map((t) => (
                      <div key={t.id} className="flex items-center justify-between text-xs">
                        <div className="flex-1 min-w-0">
                          <div className="truncate">{t.description ?? t.kind}</div>
                          <div className="text-muted-foreground">{new Date(t.created_at).toLocaleString()}</div>
                        </div>
                        <div className={`font-mono ${t.amount_cents > 0 ? "text-primary" : "text-muted-foreground"}`}>
                          {t.amount_cents > 0 ? "+" : ""}${(t.amount_cents / 100).toFixed(2)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}